import { Controller } from "@hotwired/stimulus"
import { totalFor, type Price } from "../../lib/billing/price"

export default class extends Controller {
  static targets = ["option", "amount", "summary", "continue", "savings", "savingsAmount", "annualDiscount", "submit"]
  static values = { prices: Object, quantity: Number, baseSeats: Number, saveUrl: String }

  declare optionTargets: HTMLInputElement[]
  declare amountTargets: HTMLElement[]
  declare summaryTargets: HTMLElement[]
  declare continueTargets: HTMLAnchorElement[]
  declare savingsTargets: HTMLElement[]
  declare savingsAmountTargets: HTMLElement[]
  declare annualDiscountTargets: HTMLElement[]
  declare submitTarget: HTMLInputElement
  declare hasSubmitTarget: boolean
  declare pricesValue: Record<string, Price>
  declare quantityValue: number
  declare baseSeatsValue: number
  declare saveUrlValue: string

  private pendingSave: Promise<unknown> | null = null
  private readyToProceed = false
  private submitting = false
  private submitLabel = ""

  connect() { this.render() }

  change() {
    this.render()
    const key = this.selectedKey
    const save = (this.pendingSave || Promise.resolve()).then(() => fetch(this.saveUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
        Accept: "application/json"
      },
      body: new URLSearchParams({ lookup_key: key }).toString()
    })).catch(() => {}).finally(() => {
      if (this.pendingSave === save) this.pendingSave = null
    })
    this.pendingSave = save
  }

  async proceed(event: Event) {
    if (this.readyToProceed) return
    if (this.submitting) {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }
    if (this.hasSubmitTarget) {
      this.submitting = true
      this.submitLabel = this.submitTarget.value
      this.submitTarget.disabled = true
      this.submitTarget.value = this.submitTarget.dataset.disableWith || this.submitTarget.value
    }
    if (!this.pendingSave) return
    event.preventDefault()
    event.stopImmediatePropagation()
    const target = (event.target as HTMLElement).closest("form, a") as HTMLFormElement | HTMLAnchorElement
    await this.pendingSave
    this.readyToProceed = true
    if (target instanceof HTMLFormElement) target.requestSubmit()
    else target.click()
    this.readyToProceed = false
  }

  resetSubmission() {
    if (!this.hasSubmitTarget || !this.submitting) return
    this.submitting = false
    this.submitTarget.disabled = false
    this.submitTarget.value = this.submitLabel
  }

  seatsChanged(event: CustomEvent<{ quantity: number }>) {
    this.quantityValue = event.detail.quantity
    this.render()
  }

  private get selectedKey() {
    return this.optionTargets.find(option => option.checked)?.value || "standard_monthly"
  }

  private render() {
    for (const amount of this.amountTargets) {
      const key = amount.dataset.lookupKey || this.selectedKey
      const price = this.pricesValue[key]
      amount.textContent = new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency", currency: price.currency
      }).format(totalFor(price, this.quantityValue, this.baseSeatsValue))
    }
    for (const summary of this.summaryTargets) {
      summary.hidden = summary.dataset.lookupKey !== this.selectedKey
    }
    for (const link of this.continueTargets) {
      const url = new URL(link.href)
      url.searchParams.set("lookup_key", this.selectedKey)
      link.href = url.toString()
    }
    this.renderSavings()
  }

  private renderSavings() {
    const monthly = this.pricesValue.standard_monthly
    const yearly = this.pricesValue.standard_yearly
    const monthlyCents = Math.round(totalFor(monthly, this.quantityValue, this.baseSeatsValue) * 100)
    const yearlyCents = Math.round(totalFor(yearly, this.quantityValue, this.baseSeatsValue) * 100)
    const savingsCents = monthlyCents * 12 - yearlyCents
    const hasSavings = monthly.currency === yearly.currency && savingsCents > 0
    for (const savings of this.savingsTargets) savings.hidden = !hasSavings
    for (const badge of this.annualDiscountTargets) badge.hidden = !hasSavings || !yearly.annual_discount_percent
    for (const amount of this.savingsAmountTargets) {
      amount.textContent = new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency", currency: yearly.currency
      }).format(savingsCents / 100)
    }
  }
}
